import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronDown,
  FileText,
  Link2,
  MapPin,
  Minus,
  Plus,
  User,
} from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Spinner } from "@baluu/ui/components/spinner";
import {
  UserFormStep,
  formHasRenderableQuestions,
  formHasRenderableRequiredQuestions,
} from "@baluu/ui/components/checkout/user-form-step";
import { bookingJson, messageOf } from "../../api";
import { Notice } from "../../parts";
import type {
  BundleBooking,
  BundleDoc,
  BundleTimetablePage,
  BundleTimetableSession,
  ManageLocation,
  ManageSchool,
  ManageTutor,
  UserFormAnswer,
} from "./types";
import { formatDateTime, locationName, tutorName } from "./bundleManage";
import { displayTimezone, knownTimezone } from "../../timezones";
import {
  autoIncluded,
  bookedTickets,
  bundleOptions,
  guestsPerTicket,
  maxSelectable,
  optionCapacity,
  prefillAnswers,
  previousAnswers,
  quickSelect,
  selectedGuests,
  selectedTickets,
  selectionEntries,
  sessionGuests,
  sessionTickets,
  setQuantity,
  timetableUrl,
  type Selection,
} from "./timetableSelection";
import { itemsFromSelection, type RedemptionItem } from "./redemption";

// The bundle page's Timetable tab (v1's BundleTimetable): the bundle's
// listings' bookable sessions, ten at a time between optional dates; tickets
// chosen per session within the bundle's spots, a term ticket bringing its
// term's other sessions; each session's questions (prefilled from earlier
// bookings); and "Book with bundle", which opens the confirmation.

const PAGE_SIZE = 10;

export type TimetableBooking = { items: RedemptionItem[]; spots: number; title: string };

const chip = "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap";

export function TimetableTab({
  api,
  school,
  bundle,
  courseIds,
  remaining,
  upcoming,
  past,
  locations,
  tutors,
  onBook,
}: {
  api: string;
  school: ManageSchool;
  bundle: BundleDoc;
  courseIds: string[];
  /** Spots the bundle has left (less bookings still being made). */
  remaining: number;
  upcoming: BundleBooking[];
  past: BundleBooking[];
  locations?: ManageLocation[];
  tutors?: ManageTutor[];
  onBook: (booking: TimetableBooking) => void;
}) {
  const [range, setRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [selection, setSelection] = useState<Selection>({});
  const [answers, setAnswers] = useState<Record<string, UserFormAnswer[]>>({});
  const [valid, setValid] = useState<Record<string, boolean>>({});
  const slug = school.slug ?? "";

  const timetable = useInfiniteQuery({
    queryKey: ["booking", "bundle-timetable", api, slug, courseIds.join(","), range.from, range.to],
    enabled: !!slug && courseIds.length > 0,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      bookingJson<BundleTimetablePage>(
        timetableUrl(api, { slug, courseIds, page: pageParam, limit: PAGE_SIZE, from: range.from || undefined, to: range.to || undefined }),
      ),
    getNextPageParam: (last, pages) => ((last.sessions?.length ?? 0) === PAGE_SIZE ? pages.length + 1 : undefined),
    staleTime: 60_000,
  });

  const seen = new Set<string>();
  const sessions: BundleTimetableSession[] = [];
  for (const page of timetable.data?.pages ?? []) {
    for (const session of page.sessions ?? []) {
      if (!seen.has(session._id)) {
        seen.add(session._id);
        sessions.push(session);
      }
    }
  }

  const history = [...upcoming, ...past];
  const previous = previousAnswers(history);
  const included = autoIncluded(sessions, selection);
  const guests = selectedGuests(sessions, selection);
  const tickets = selectedTickets(selection);
  const zoneOf = (session: BundleTimetableSession) => displayTimezone(session.course?.timezone, school.timezone);

  const clear = () => {
    setSelection({});
    setAnswers({});
    setValid({});
  };

  const changeRange = (next: { from: string; to: string }) => {
    setRange(next);
    // The sessions the selection points at may no longer be listed.
    clear();
  };

  const changeQuantity = (session: BundleTimetableSession, ticketTypeID: string, quantity: number) => {
    if (quantity > 0 && !answers[session._id]) {
      const others = Object.values(answers).flat();
      const prefilled = prefillAnswers(session.course?.userQuestionForm?.questions, others, previous);
      if (prefilled.length > 0) setAnswers((a) => ({ ...a, [session._id]: prefilled }));
    }
    setSelection((s) => setQuantity(s, session._id, ticketTypeID, quantity));
  };

  const pickAll = (checked: boolean) => {
    if (!checked) {
      clear();
      return;
    }
    const picked = quickSelect(sessions, remaining, upcoming);
    const filled: Record<string, UserFormAnswer[]> = {};
    for (const id of Object.keys(picked)) {
      const session = sessions.find((s) => s._id === id);
      const prefilled = prefillAnswers(session?.course?.userQuestionForm?.questions, Object.values(filled).flat(), previous);
      if (prefilled.length > 0) filled[id] = prefilled;
    }
    setSelection(picked);
    setAnswers(filled);
    setValid({});
  };

  const questionsBlock = sessions.some((session) => {
    if (!selection[session._id]) return false;
    return formHasRenderableRequiredQuestions(session.course?.userQuestionForm, sessionGuests(session, selection)) && !valid[session._id];
  });

  const book = () => {
    if (guests === 0 || questionsBlock) return;
    const entries = selectionEntries(sessions, selection);
    const items = itemsFromSelection(entries, {
      school,
      bundleId: bundle.id,
      answers,
      timezoneFor: (entry) => knownTimezone(entry.session.course?.timezone, school.timezone),
    });
    const titles = Array.from(new Set(entries.map((e) => e.session.course?.title).filter(Boolean)));
    const sessionCount = new Set(entries.map((e) => e.session._id)).size;
    const title = sessionCount === 1 && titles[0] ? `${titles[0]} · ${formatDateTime(entries[0].session.startDateTime, zoneOf(entries[0].session))}` : `${sessionCount} sessions`;
    onBook({ items, spots: guests, title });
  };

  if (courseIds.length === 0) {
    return <p className="py-8 text-center text-muted-foreground">No services available at the moment</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-end">
          <div className="space-y-1">
            <Label htmlFor="bundle-from" className="text-xs">
              From
            </Label>
            <Input
              id="bundle-from"
              type="date"
              value={range.from}
              min={todayIso()}
              max={range.to || undefined}
              onChange={(e) => changeRange({ ...range, from: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bundle-to" className="text-xs">
              To
            </Label>
            <Input
              id="bundle-to"
              type="date"
              value={range.to}
              min={range.from || todayIso()}
              onChange={(e) => changeRange({ ...range, to: e.target.value })}
            />
          </div>
          {(range.from || range.to) && (
            <Button variant="ghost" size="sm" onClick={() => changeRange({ from: "", to: "" })}>
              Clear dates
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground sm:ml-auto">
          {sessions.length} session{sessions.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {timetable.isError && (
        <Notice tone="error">
          {messageOf(timetable.error, "We couldn’t load the timetable. Please try again.")}{" "}
          <button type="button" className="font-medium underline" onClick={() => timetable.refetch()}>
            Try again
          </button>
        </Notice>
      )}

      {timetable.isLoading ? (
        <div className="flex flex-col items-center py-12 text-sm text-muted-foreground" aria-busy="true">
          <Spinner className="h-8 w-8" />
          <p className="mt-4">Loading available sessions…</p>
        </div>
      ) : sessions.length === 0 ? (
        !timetable.isError && (
          <div className="py-12 text-center text-muted-foreground">
            <CalendarIcon className="mx-auto mb-3 h-12 w-12 opacity-30" aria-hidden />
            <p className="mb-1 text-base font-medium">No upcoming sessions available</p>
            <p className="text-sm">Check back later for new session dates</p>
          </div>
        )
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b py-2">
            <div className="flex items-center gap-2">
              <Checkbox id="bundle-quick-select" checked={tickets > 0} onCheckedChange={(v) => pickAll(v === true)} disabled={remaining <= 0 && tickets === 0} />
              <Label htmlFor="bundle-quick-select" className="cursor-pointer text-sm">
                Quick select sessions
              </Label>
            </div>
            <span className="text-xs font-medium">
              {guests}/{Math.max(0, remaining)} spots selected
            </span>
          </div>

          <ul className="space-y-3">
            {sessions.map((session) => {
              const booked = bookedTickets(session._id, upcoming);
              const auto = included[session._id];
              const selected = !!selection[session._id];
              const sessionGuestCount = sessionGuests(session, selection);
              const sessionTicketCount = sessionTickets(session._id, selection);
              const options = bundleOptions(session, auto);
              const where = session.location ? (session.isOnline ? "Online" : locationName(locations, session.location)) : undefined;
              const tutor = tutorName(tutors, session.tutor);
              const form = session.course?.userQuestionForm;
              const tone = booked
                ? "border-green-300 bg-green-50/40"
                : auto
                  ? "border-purple-300 bg-purple-50/50"
                  : selected
                    ? "border-blue-300 bg-blue-50/50"
                    : "border-border";
              return (
                <li key={session._id} className={`rounded-lg border p-4 ${tone}`}>
                  <div className="mb-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="mb-1 font-medium">
                        {session.course?.title}
                        {session.termName && <span className="ml-2 text-sm font-normal text-muted-foreground">({session.termName})</span>}
                      </h4>
                      <p className="mb-1 flex items-center gap-1 text-sm font-bold text-muted-foreground">
                        <CalendarIcon className="h-3 w-3" aria-hidden />
                        {formatDateTime(session.startDateTime, zoneOf(session))}
                      </p>
                      {where && (
                        <p className="mb-1 flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" aria-hidden />
                          {where}
                        </p>
                      )}
                      {tutor && (
                        <p className="flex items-center gap-1 text-sm text-muted-foreground">
                          <User className="h-3 w-3" aria-hidden />
                          with {tutor}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {auto && (
                        <span className={`${chip} bg-purple-100 text-purple-700`}>
                          <Link2 className="mr-1 h-3 w-3" aria-hidden />
                          Included in {auto.ticketTypeName ?? "your term"}
                        </span>
                      )}
                      {booked > 0 && (
                        <span className={`${chip} bg-green-100 text-green-700`}>
                          <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
                          {booked} ticket{booked !== 1 ? "s" : ""} booked
                        </span>
                      )}
                      {session.availability !== undefined &&
                        (session.availability <= 0 ? (
                          <span className={`${chip} bg-red-100 text-red-700`}>Sold out</span>
                        ) : (
                          session.availability <= 3 && (
                            <span className={`${chip} bg-orange-100 text-orange-700`}>Only {session.availability} spot(s) left</span>
                          )
                        ))}
                    </div>
                  </div>

                  {auto && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg border-t border-purple-200 bg-purple-50 p-3 text-sm text-purple-800">
                      <Link2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      <div>
                        <p className="font-medium">Automatically included</p>
                        <p className="mt-1 text-xs">
                          This session is part of the same term as your selected booking and will be included automatically.
                        </p>
                      </div>
                    </div>
                  )}

                  {options.length > 0 && (
                    <div className="space-y-2 border-t pt-3">
                      <p className="mb-2 text-sm font-medium">Booking options</p>
                      {options.map((option) => {
                        const id = option.ticketTypeID!;
                        const capacity = optionCapacity(option, session, remaining, booked);
                        if (capacity <= 0) return null;
                        const current = selection[session._id]?.[id] ?? 0;
                        const max = maxSelectable(option, session, sessions, selection, remaining, booked);
                        const per = guestsPerTicket(option);
                        const name = option.ticketTypeName || "Ticket";
                        return (
                          <div key={id} className="flex items-center justify-between rounded-md bg-muted/50 p-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium capitalize">{name}</span>
                                {per > 1 && <span className={`${chip} bg-purple-100 text-purple-700`}>{per} spots/ticket</span>}
                              </div>
                              {!!option.maxLimit && <p className="text-xs text-muted-foreground">{capacity} available</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={`One fewer ${name}`}
                                disabled={current <= 0}
                                onClick={() => changeQuantity(session, id, Math.max(0, current - 1))}
                              >
                                <Minus className="h-4 w-4" aria-hidden />
                              </Button>
                              <span className="min-w-[2rem] text-center text-sm font-medium" aria-live="polite">
                                {current}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={`One more ${name}`}
                                disabled={current >= max}
                                onClick={() => changeQuantity(session, id, current + 1)}
                              >
                                <Plus className="h-4 w-4" aria-hidden />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {sessionTicketCount > 0 && (
                        <p className="text-xs font-medium text-blue-700">
                          {sessionTicketCount} ticket{sessionTicketCount !== 1 ? "s" : ""} selected
                          {sessionGuestCount !== sessionTicketCount && (
                            <span className="text-muted-foreground">
                              {" "}
                              ({sessionGuestCount} spot{sessionGuestCount !== 1 ? "s" : ""})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  {selected && formHasRenderableQuestions(form, sessionGuestCount) && form?.questions && (
                    <div className="mt-3 border-t pt-4">
                      <div className="mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
                        <span className="text-sm font-medium">Customer information</span>
                        {previous.length > 0 && (
                          <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">Pre-filled from previous booking</span>
                        )}
                      </div>
                      <UserFormStep
                        questions={form.questions}
                        values={answers[session._id] ?? []}
                        mode={form.mode || "all"}
                        totalGuests={sessionGuestCount}
                        onChange={(values) => setAnswers((a) => ({ ...a, [session._id]: values }))}
                        onValidationChange={(isValid) =>
                          setValid((v) => (v[session._id] === isValid ? v : { ...v, [session._id]: isValid }))
                        }
                      />
                    </div>
                  )}

                  {booked > 0 && (
                    <p className="mt-3 flex items-center gap-2 border-t pt-3 text-sm font-medium text-green-700">
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                      You have {booked} ticket{booked !== 1 ? "s" : ""} booked for this session
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          {timetable.hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => timetable.fetchNextPage()} disabled={timetable.isFetchingNextPage}>
                {timetable.isFetchingNextPage ? (
                  <>
                    <Spinner className="h-4 w-4" />
                    Loading…
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" aria-hidden />
                    Load more sessions
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {guests > 0 && (
        <div className="sticky bottom-0 z-10 -mx-6 border-t bg-background px-6 py-4 shadow-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-700" aria-hidden />
              <div>
                <p className="text-sm font-medium">
                  {guests} spot{guests !== 1 ? "s" : ""} selected
                  {tickets !== guests && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      ({tickets} ticket{tickets !== 1 ? "s" : ""})
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{Math.max(0, remaining - guests)} remaining in bundle</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" onClick={clear}>
                Clear
              </Button>
              <Button onClick={book} disabled={questionsBlock}>
                {questionsBlock ? "Complete required questions" : `Book with bundle (${guests})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Today as "YYYY-MM-DD" in the viewer's calendar (a date input's min). */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
